import type { Language } from '../types'
import { LANGUAGES, SUPPORTED_LANGUAGES } from '../config/languages'

// Private helper function since it's only used internally
const getLanguageByCode = (code: Language) => LANGUAGES.find((lang) => lang.code === code)

export const getLanguageNativeName = (code: Language) => getLanguageByCode(code)?.nativeName || code

export const isLanguage = (code: string): code is Language =>
  SUPPORTED_LANGUAGES.includes(code as Language)

// Helper function to clean path from language prefixes
const cleanPathFromLanguagePrefixes = (path: string): string => {
  const pathSegments = path.split('/').filter(Boolean)
  let cleanedPath = path

  // Remove first language prefix if present
  if (pathSegments.length > 0 && isLanguage(pathSegments[0])) {
    cleanedPath = '/' + pathSegments.slice(1).join('/')
  }

  // Remove second language prefix if present (double language case)
  const remainingSegments = cleanedPath.split('/').filter(Boolean)
  if (remainingSegments.length > 0 && isLanguage(remainingSegments[0])) {
    cleanedPath = '/' + remainingSegments.slice(1).join('/')
  }

  return cleanedPath
}

export const getAlternateLanguages = (path: string) => {
  const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin
  const pathWithoutLang = cleanPathFromLanguagePrefixes(path)

  return SUPPORTED_LANGUAGES.map((code) => ({
    hreflang: code,
    href: `${baseUrl}/${code}${pathWithoutLang}`
  }))
}

export const getCanonicalUrl = (path: string) => {
  const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin
  const cleanedPath = cleanPathFromLanguagePrefixes(path)

  // If the path was cleaned (had language prefixes), use the cleaned version
  if (cleanedPath !== path) {
    return `${baseUrl}${cleanedPath}`
  }

  return `${baseUrl}${path}`
}
